/**
 * ChatShellPage — skill-launched chat experience.
 *
 * Mounted at /chat/new and /chat/:threadId.
 *
 * URL params:
 *   skill    – skill_id (shown in header badge)
 *   client   – client_uid (context for the session)
 *   persona  – persona_id (context for the session)
 *   prompt   – URL-encoded trigger_prompt; fired automatically on mount
 *
 * The page connects to POST /api/v1/chat/stream (SSE) and renders:
 *   - streaming text bubbles
 *   - tool-call indicators (collapsible)
 *   - React artifacts (sandboxed iframe)
 *   - Brief artifact cards
 *   - Interrupt prompts (human-in-the-loop)
 */

import {
  AlertTriangle,
  ArrowLeft,
  BarChart2,
  Bot,
  ChevronDown,
  ChevronRight,
  Code2,
  Columns,
  Eye,
  FileText,
  GitBranch,
  Layers,
  Loader2,
  Maximize2,
  PenLine,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { agentLabApi, getApiBase } from '@/services/api';
import { SkillsRail } from '@/components/SkillsRail';
import { ChartBubble } from '@/components/chat/ChartBubble';
import { useCreativeSlashMenu } from '@/components/CreativeSlashMenu';
import {
  type ChatLlmProvider,
  readChatLlmProviderCookie,
  writeChatLlmProviderCookie,
} from '@/lib/chatLlmProviderCookie';

// ---------------------------------------------------------------------------
// Provider config
// ---------------------------------------------------------------------------

interface ProviderConfig {
  id: ChatLlmProvider;
  label: string;
  model: string;
  /** Inline SVG path(s) used as the provider icon */
  icon: React.ReactNode;
}

// Minimal SVG brand icons (monochrome, scaled to fit 16×16 viewBox)
function OpenAIIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M22.28 9.98a5.82 5.82 0 0 0-.5-4.78 5.9 5.9 0 0 0-6.35-2.83A5.87 5.87 0 0 0 11.01 1a5.9 5.9 0 0 0-5.63 4.09 5.87 5.87 0 0 0-3.92 2.84 5.9 5.9 0 0 0 .73 6.91 5.82 5.82 0 0 0 .5 4.78 5.9 5.9 0 0 0 6.35 2.83A5.87 5.87 0 0 0 12.99 23a5.9 5.9 0 0 0 5.63-4.09 5.87 5.87 0 0 0 3.92-2.84 5.9 5.9 0 0 0-.73-6.91l-.53.82zM13 21.5a4.4 4.4 0 0 1-2.82-1.02l.14-.08 4.68-2.7a.77.77 0 0 0 .39-.67v-6.6l1.98 1.14a.07.07 0 0 1 .04.05v5.46A4.42 4.42 0 0 1 13 21.5zM3.63 17.72a4.4 4.4 0 0 1-.53-2.96l.14.08 4.68 2.7a.77.77 0 0 0 .77 0l5.71-3.3v2.28a.07.07 0 0 1-.03.06L9.7 19.3a4.42 4.42 0 0 1-6.07-1.58zm-.9-9.67A4.4 4.4 0 0 1 5.02 5.8v5.54a.77.77 0 0 0 .39.67l5.71 3.3-1.98 1.14a.07.07 0 0 1-.07 0L4.4 13.7a4.42 4.42 0 0 1-1.67-5.65zm16.28 3.78-5.71-3.3 1.98-1.14a.07.07 0 0 1 .07 0l4.67 2.7a4.42 4.42 0 0 1-.68 7.97v-5.54a.77.77 0 0 0-.33-.69zm1.97-2.97-.14-.08-4.68-2.7a.77.77 0 0 0-.77 0L9.68 9.38V7.1a.07.07 0 0 1 .03-.06l4.67-2.7a4.42 4.42 0 0 1 6.6 4.58zM8.74 13.15l-1.98-1.14a.07.07 0 0 1-.04-.05V6.5a4.42 4.42 0 0 1 7.25-3.39l-.14.08-4.68 2.7a.77.77 0 0 0-.39.67l-.02 6.6zm1.07-2.32 2.54-1.47 2.54 1.47v2.93l-2.54 1.47-2.54-1.47v-2.93z" />
    </svg>
  );
}

function AnthropicIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M13.827 3.52h3.603L24 20h-3.603l-6.57-16.48zm-3.654 0H6.57L0 20h3.603l1.378-3.504h6.907l1.378 3.504h3.603L10.173 3.52zm-1.28 9.538 2.27-5.808 2.27 5.808H8.893z" />
    </svg>
  );
}

function GeminiIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.305 14.305 0 0 0 12 12 14.305 14.305 0 0 0-12 12" />
    </svg>
  );
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'openai',
    label: 'ChatGPT',
    model: 'gpt-5.5',
    icon: <OpenAIIcon className="w-3.5 h-3.5" />,
  },
  {
    id: 'anthropic',
    label: 'Claude',
    model: 'claude-opus-4-7',
    icon: <AnthropicIcon className="w-3.5 h-3.5" />,
  },
  {
    id: 'google',
    label: 'Gemini',
    model: 'gemini-3.1-pro-preview',
    icon: <GeminiIcon className="w-3.5 h-3.5" />,
  },
];

function ProviderToggle({
  value,
  onChange,
}: {
  value: ChatLlmProvider;
  onChange: (p: ChatLlmProvider) => void;
}) {
  const [hovered, setHovered] = useState<ChatLlmProvider | null>(null);

  return (
    <div className="relative flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5">
      {PROVIDERS.map((p) => (
        <div key={p.id} className="relative">
          <button
            onClick={() => onChange(p.id)}
            onMouseEnter={() => setHovered(p.id)}
            onMouseLeave={() => setHovered(null)}
            className={clsx(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all',
              value === p.id
                ? 'bg-white shadow-sm text-slate-800'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {p.icon}
            <span className="hidden sm:inline">{p.label}</span>
          </button>

          {hovered === p.id && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
              <div className="rounded-md bg-slate-900 px-2.5 py-1.5 text-center shadow-lg whitespace-nowrap">
                <p className="text-[11px] font-semibold text-white">{p.label}</p>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.model}</p>
              </div>
              {/* Arrow */}
              <div className="flex justify-center">
                <div className="w-2 h-2 bg-slate-900 rotate-45 -mt-1" />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ToolCall {
  name: string;
  status: 'running' | 'done' | 'error';
  resultSummary?: string;
  args?: Record<string, unknown>;
}

interface Artifact {
  uri: string;
  mime: string;
  filename: string;
  kind: string;
}

interface SkillStep {
  phase: string;
  label: string;
  status: 'running' | 'done';
}

type ChatEntry =
  | { id: string; role: 'user'; content: string; type?: undefined }
  | {
      id: string;
      role: 'assistant';
      type?: undefined;
      content: string;
      toolCalls: ToolCall[];
      artifacts: Artifact[];
      skillSteps: SkillStep[];
      isStreaming?: boolean;
    }
  | {
      // Chart-analytics-ui — `chart_output` SSE event renders inline as a
      // ChartBubble between text bubbles. The ordering rule (chart before the
      // narrative `message_delta`) is preserved by the message stream.
      id: string;
      role: 'assistant';
      type: 'chart';
      chart: import('@/services/api').WorkbenchChartSpec;
      narrative?: string;
    };

function isChartEntry(
  m: ChatEntry,
): m is Extract<ChatEntry, { type: 'chart' }> {
  return m.role === 'assistant' && m.type === 'chart';
}

function isAssistantTextEntry(
  m: ChatEntry,
): m is Extract<ChatEntry, { role: 'assistant'; type?: undefined }> {
  return m.role === 'assistant' && m.type !== 'chart';
}

interface InterruptPayload {
  interrupt_id: string;
  kind: string;
  payload: { value?: unknown; [k: string]: unknown };
}

// ---------------------------------------------------------------------------
// SSE parser
// ---------------------------------------------------------------------------

function parseSseChunk(buf: string): { event: string; data: unknown }[] {
  const events: { event: string; data: unknown }[] = [];
  const blocks = buf.split('\n\n');
  for (const block of blocks) {
    if (!block.trim()) continue;
    let eventName = '';
    let dataStr = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) eventName = line.slice(7).trim();
      else if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
    }
    if (!eventName || !dataStr) continue;
    try {
      events.push({ event: eventName, data: JSON.parse(dataStr) });
    } catch {
      // malformed — skip
    }
  }
  return events;
}

/** Flatten streamed LLM chunks (Gemini: arrays of typed blocks, never append raw objects). */
function flattenStreamDelta(delta: unknown): string {
  if (delta == null) return '';
  if (typeof delta === 'string') return delta;
  if (typeof delta === 'number' || typeof delta === 'boolean') return String(delta);
  if (Array.isArray(delta)) {
    return delta.map(flattenStreamDelta).join('');
  }
  if (typeof delta === 'object') {
    const o = delta as Record<string, unknown>;
    const typ = o.type;
    if (typ === 'thinking' || typ === 'reasoning') return '';
    if (typeof o.text === 'string') return o.text;
    if (typ === 'text' && typeof o.text === 'string') return o.text;
    try {
      return JSON.stringify(o);
    } catch {
      return '';
    }
  }
  return '';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Safe display for tool args/results (avoids "[object Object]" from stray non-strings). */
function stringifyUnknown(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function ToolGlyph({ name }: { name: string }) {
  const n = name.toLowerCase();
  if (n.includes('bonnard') || n.includes('schema') || n.includes('sql')) {
    return <BarChart2 size={13} className="text-violet-500 shrink-0" aria-hidden />;
  }
  if (n.includes('web_search') || n === 'search') {
    return <Search size={13} className="text-sky-500 shrink-0" aria-hidden />;
  }
  return <Sparkles size={13} className="text-teal-500 shrink-0" aria-hidden />;
}

function SkillStepsStrip({ steps }: { steps: SkillStep[] }) {
  if (!steps.length) return null;
  return (
    <div className="mb-3 rounded-xl border border-teal-100/80 bg-gradient-to-r from-teal-50/90 via-white to-slate-50/80 px-3 py-2.5 shadow-sm">
      <p className="text-[10px] uppercase tracking-wider text-teal-600/90 font-semibold mb-2">Execution</p>
      <ul className="space-y-1.5">
        {steps.map((s, i) => (
          <li key={`${s.phase}-${i}`} className="flex items-start gap-2 text-[11px] text-slate-600">
            <span
              className={clsx(
                'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold',
                s.status === 'done' ? 'bg-emerald-500 text-white' : 'bg-brand-teal/15 text-brand-teal animate-pulse',
              )}
            >
              {s.status === 'done' ? '✓' : '…'}
            </span>
            <span className="leading-snug">
              <span className="font-medium text-slate-700">{s.label}</span>
              {s.phase ? (
                <span className="text-slate-400 font-mono text-[10px] ml-1">({s.phase})</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToolCallBubble({ tc }: { tc: ToolCall }) {
  const [open, setOpen] = useState(false);

  const statusIcon =
    tc.status === 'running' ? (
      <Loader2 size={12} className="animate-spin text-brand-teal" />
    ) : tc.status === 'done' ? (
      <span className="text-emerald-500 text-[10px] font-bold">✓</span>
    ) : (
      <span className="text-red-400 text-[10px] font-bold">✗</span>
    );

  const argKeys = tc.args && typeof tc.args === 'object' ? Object.keys(tc.args as object) : [];
  const hasDetail = argKeys.length > 0 || Boolean(tc.resultSummary);
  const resultText = stringifyUnknown(tc.resultSummary);

  return (
    <div
      className={clsx(
        'rounded-xl border overflow-hidden text-xs shadow-sm transition-shadow',
        tc.status === 'running'
          ? 'border-teal-200/80 bg-gradient-to-br from-white via-teal-50/30 to-slate-50/90 shadow-teal-500/5'
          : 'border-slate-200/70 bg-gradient-to-br from-white to-slate-50/80',
      )}
    >
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        className={clsx(
          'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors',
          hasDetail ? 'hover:bg-slate-50/80 cursor-pointer' : 'cursor-default',
        )}
      >
        <ToolGlyph name={tc.name} />
        <span className="font-mono text-[11px] text-slate-700 flex-1 truncate tracking-tight">{tc.name}</span>
        <span className="flex items-center gap-1 shrink-0">
          {statusIcon}
          {hasDetail && (
            <ChevronDown
              size={12}
              className={clsx('text-slate-400 transition-transform', open && 'rotate-180')}
            />
          )}
        </span>
      </button>

      {open && hasDetail && (
        <div className="border-t border-slate-100/90 px-3 py-2.5 space-y-2 bg-white/60">
          {argKeys.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 font-semibold">Input</p>
              <pre className="text-[11px] text-slate-600 whitespace-pre-wrap break-all font-mono bg-slate-50 rounded-lg border border-slate-100 p-2 max-h-48 overflow-y-auto">
                {stringifyUnknown(tc.args)}
              </pre>
            </div>
          )}
          {resultText ? (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 font-semibold">Result</p>
              <p className="text-[11px] text-slate-600 whitespace-pre-wrap break-words font-mono bg-slate-50 rounded-lg border border-slate-100 p-2 max-h-64 overflow-y-auto leading-relaxed">
                {resultText}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkillTracePanel — collapsible execution trace for agentic skill runs
// ---------------------------------------------------------------------------

const PHASE_ICONS: Record<string, React.ElementType> = {
  init: Zap,
  data_load: Search,
  progress: Loader2,
  synthesis: GitBranch,
  done: TrendingUp,
};

const PHASE_COLORS: Record<string, string> = {
  init: 'text-brand-teal',
  data_load: 'text-blue-500',
  progress: 'text-amber-500',
  synthesis: 'text-purple-500',
  done: 'text-emerald-500',
};

function SkillTracePanel({
  steps,
  toolCalls,
  isStreaming,
}: {
  steps: SkillStep[];
  toolCalls: ToolCall[];
  isStreaming?: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (steps.length === 0 && toolCalls.length === 0) return null;

  const doneSteps = steps.filter((s) => s.status === 'done').length;
  const totalSteps = steps.length;
  const runningStep = [...steps].reverse().find((s) => s.status === 'running');
  const isDone = !isStreaming || steps.some((s) => s.phase === 'done');

  const summaryLabel = runningStep
    ? runningStep.label
    : isDone
    ? `Completed ${totalSteps} phases · ${toolCalls.length} queries`
    : `${doneSteps}/${totalSteps} phases`;

  return (
    <div className="mb-3 rounded-lg border border-slate-100 bg-slate-50/80 overflow-hidden text-xs">
      {/* Header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {isStreaming && !isDone ? (
            <Loader2 size={11} className="animate-spin text-brand-teal shrink-0" />
          ) : (
            <GitBranch size={11} className="text-brand-teal shrink-0" />
          )}
          <span className="font-semibold text-slate-600 truncate">
            {isStreaming && !isDone ? 'Agent executing…' : 'Execution trace'}
          </span>
          {!isStreaming && (
            <span className="text-slate-400 text-[10px] font-normal truncate ml-1">
              {summaryLabel}
            </span>
          )}
          {isStreaming && runningStep && (
            <span className="text-brand-teal text-[10px] font-medium truncate ml-1 animate-pulse">
              {runningStep.label}
            </span>
          )}
        </div>
        <ChevronDown
          size={11}
          className={clsx('text-slate-400 transition-transform shrink-0', open && 'rotate-180')}
        />
      </button>

      {/* Expanded trace */}
      {open && (
        <div className="border-t border-slate-100 px-3 py-2 space-y-2">
          {/* Skill phases */}
          {steps.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                Phases
              </p>
              {steps.map((step, i) => {
                const Icon = PHASE_ICONS[step.phase] ?? Zap;
                const colorClass = PHASE_COLORS[step.phase] ?? 'text-slate-400';
                return (
                  <div key={i} className="flex items-center gap-2 py-0.5">
                    {step.status === 'running' ? (
                      <Loader2 size={10} className="animate-spin text-brand-teal shrink-0" />
                    ) : (
                      <Icon size={10} className={clsx(colorClass, 'shrink-0')} />
                    )}
                    <span className={clsx('text-[11px]', step.status === 'done' ? 'text-slate-600' : 'text-slate-400')}>
                      {step.label}
                    </span>
                    {step.status === 'done' && (
                      <span className="text-emerald-500 text-[10px] font-bold ml-auto">✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Tool calls inline */}
          {toolCalls.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mt-1">
                Tool calls ({toolCalls.length})
              </p>
              {toolCalls.map((tc, i) => (
                <ToolCallBubble key={`${tc.name}-${i}`} tc={tc} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ArtifactResizeHandle({
  onResize,
}: {
  onResize: (delta: number) => void;
}) {
  const dragging = useRef(false);
  const lastY = useRef(0);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientY - lastY.current;
      lastY.current = e.clientY;
      onResize(delta);
    };
    const handleUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [onResize]);

  return (
    <div
      onMouseDown={(e) => {
        dragging.current = true;
        lastY.current = e.clientY;
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
      }}
      title="Drag to resize"
      className="h-2 w-full cursor-ns-resize bg-slate-50 border-t border-slate-100 hover:bg-slate-100 transition-colors"
    />
  );
}

function ReactArtifact({ uri, filename }: { uri: string; filename?: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [height, setHeight] = useState(540);

  useEffect(() => {
    if (!uri) return;
    fetch(uri)
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        return r.text();
      })
      .then((code) => {
        setHtml(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <script src="https://unpkg.com/react@18/umd/react.development.js" crossorigin></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js" crossorigin></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <link href="https://cdn.jsdelivr.net/npm/tailwindcss@3/dist/tailwind.min.css" rel="stylesheet">
  <style>body{margin:0;background:#fff}</style>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel">
${code}
const _root = ReactDOM.createRoot(document.getElementById('root'));
_root.render(React.createElement(typeof App !== 'undefined' ? App : 'div', null, 'Artifact loaded'));
  </script>
</body>
</html>`);
      })
      .catch(() => setError(true));
  }, [uri]);

  if (error)
    return (
      <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-600">
        <AlertTriangle size={14} />
        Failed to load artifact{filename ? `: ${filename}` : ''}.
      </div>
    );

  if (!html)
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 p-4 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" />
        Loading artifact…
      </div>
    );

  return (
    <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2">
        <Code2 size={13} className="text-brand-teal" />
        <span className="text-[11px] font-medium text-slate-500 flex-1 truncate">
          {filename || 'Interactive Artifact'}
        </span>
        <button
          title="Open fullscreen"
          onClick={() => window.open(uri, '_blank')}
          className="text-slate-400 hover:text-slate-700 transition-colors"
        >
          <Maximize2 size={13} />
        </button>
      </div>
      <iframe
        srcDoc={html}
        sandbox="allow-scripts"
        title={filename || 'Skill artifact'}
        className="w-full"
        style={{ height }}
      />
      <ArtifactResizeHandle onResize={(delta) => setHeight((h) => Math.max(200, h + delta))} />
    </div>
  );
}

interface PerformanceTablePayload {
  title?: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
}

function PerformanceTableArtifact({ artifact }: { artifact: Artifact }) {
  const [data, setData] = useState<PerformanceTablePayload | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!artifact.uri) return;
    fetch(artifact.uri)
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then(setData)
      .catch(() => setError(true));
  }, [artifact.uri]);

  if (error)
    return (
      <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">
        <AlertTriangle size={14} />
        Failed to load performance table.
      </div>
    );

  if (!data)
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" />
        Loading performance data…
      </div>
    );

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2">
        <BarChart2 size={13} className="text-brand-teal" />
        <span className="text-[11px] font-medium text-slate-500">
          {data.title || artifact.filename || 'Performance'}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              {data.columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-2 text-left font-semibold text-slate-400 uppercase tracking-wider text-[10px]"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr
                key={i}
                className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
              >
                {data.columns.map((col) => (
                  <td key={col} className="px-4 py-2 text-slate-700">
                    {String(row[col] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BriefArtifact({ artifact }: { artifact: Artifact }) {
  const briefId = artifact.uri.split('/').pop() ?? '';
  return (
    <Link
      to={`/creative/briefs/${briefId}`}
      className="flex items-center gap-3 rounded-xl border border-brand-teal/20 bg-brand-teal/5 px-4 py-3 hover:bg-brand-teal/10 transition-colors"
    >
      <FileText size={18} className="text-brand-teal shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-navy-900 truncate">
          {artifact.filename || 'Generated Brief'}
        </div>
        <div className="text-xs text-slate-500">Click to open in Brief Studio</div>
      </div>
      <ChevronRight size={16} className="ml-auto text-brand-teal shrink-0" />
    </Link>
  );
}

function ArtifactRenderer({ artifact }: { artifact: Artifact }) {
  if (artifact.mime === 'text/x-react' || artifact.kind === 'react_artifact') {
    return <ReactArtifact uri={artifact.uri} filename={artifact.filename} />;
  }
  if (
    artifact.mime === 'application/x-neurograph-brief' ||
    artifact.kind === 'brief'
  ) {
    return <BriefArtifact artifact={artifact} />;
  }
  if (
    artifact.mime === 'application/x-neurograph-table' ||
    artifact.kind === 'performance_table'
  ) {
    return <PerformanceTableArtifact artifact={artifact} />;
  }
  // Generic download tile
  return (
    <a
      href={artifact.uri}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 hover:bg-slate-100 transition-colors"
    >
      <Zap size={14} className="text-slate-400 shrink-0" />
      <span className="truncate">{artifact.filename || artifact.uri}</span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// SkillCompleteBar — post-skill quick actions
// ---------------------------------------------------------------------------

interface QuickAction {
  id: string;
  label: string;
  Icon: React.ElementType;
  prompt: string;
}

const POST_ACTION_CATALOG: Record<string, QuickAction[]> = {
  ask_persona: [
    {
      id: 'draft_brief',
      label: 'Draft a brief from this',
      Icon: FileText,
      prompt:
        'Based on the persona insights above, draft a creative brief with 3 angles.',
    },
    {
      id: 'explore_scenarios',
      label: 'Explore decision map',
      Icon: GitBranch,
      prompt:
        'Show me the Persona × Scenario Explorer artifact for this client/persona.',
    },
    {
      id: 'find_gaps',
      label: 'Find coverage gaps',
      Icon: AlertTriangle,
      prompt:
        'Which buyer scenarios have zero or thin creative coverage for this persona?',
    },
  ],
  scenario_explorer: [
    {
      id: 'draft_brief',
      label: 'Draft a brief for top scenario',
      Icon: FileText,
      prompt:
        'Draft a brief targeting the highest-ranked buyer scenario from the explorer.',
    },
    {
      id: 'keyword_strategy',
      label: 'Pull keyword strategy',
      Icon: Search,
      prompt:
        'Run the SEO keyword strategy for this persona using the scenarios shown.',
    },
  ],
  suggest_briefs: [
    {
      id: 'draft_top',
      label: 'Draft the top idea',
      Icon: FileText,
      prompt:
        'Draft the first brief idea above into a full structured creative brief.',
    },
    {
      id: 'compare',
      label: 'Compare to existing briefs',
      Icon: Columns,
      prompt:
        'How does idea #1 compare to our existing active briefs for this persona?',
    },
  ],
  draft_brief: [
    {
      id: 'generate_angles',
      label: 'Generate angles',
      Icon: Layers,
      prompt: 'Generate 4 creative angles from this brief.',
    },
    {
      id: 'review',
      label: 'Review through persona',
      Icon: Eye,
      prompt: "Run a creative review of this brief through the persona's eyes.",
    },
  ],
  creative_review: [
    {
      id: 'revise',
      label: 'Suggest revisions',
      Icon: PenLine,
      prompt:
        'Based on the review above, suggest 3 specific revisions to improve resonance.',
    },
    {
      id: 'generate_alt',
      label: 'Generate an alternative',
      Icon: RefreshCw,
      prompt:
        'Generate an alternative creative angle that addresses the friction points identified.',
    },
  ],

  // ---------------------------------------------------------------------------
  // Meta Ads sub-skills — post-completion action chains
  // ---------------------------------------------------------------------------

  meta_diagnose_perf: [
    {
      id: 'meta_fatigue_scan',
      label: 'Run fatigue scan',
      Icon: TrendingUp,
      prompt:
        'Now run Workflow 5 (Creative Fatigue Detection) — check all active formulas for frequency saturation and daily trajectory signals.',
    },
    {
      id: 'creative_review',
      label: 'Review underperforming ad',
      Icon: Eye,
      prompt:
        'Review the lowest-performing ad identified above through the persona lens — what is driving the disconnect?',
    },
  ],

  meta_fatigue_scan: [
    {
      id: 'generate_angles',
      label: 'Generate replacement angles',
      Icon: Layers,
      prompt:
        'Generate 4 creative angles to replace the fatigued formula — grounded in the persona\'s top buyer scenarios.',
    },
    {
      id: 'draft_brief',
      label: 'Draft a refresh brief',
      Icon: FileText,
      prompt:
        'Draft a creative brief to refresh the fatigued formula — use the persona\'s strongest PDO concept activations as the strategic foundation.',
    },
    {
      id: 'creative_review',
      label: 'Review the fatigued ad',
      Icon: Eye,
      prompt:
        'Run a creative review of the top fatigued ad through the persona lens — what signals are driving the fatigue?',
    },
  ],

  meta_coverage_mine: [
    {
      id: 'suggest_briefs',
      label: 'Suggest briefs for gaps',
      Icon: FileText,
      prompt:
        'Suggest 3 creative brief ideas to address the buyer scenario coverage gaps identified — one per uncovered scenario.',
    },
    {
      id: 'draft_brief',
      label: 'Draft brief for top gap',
      Icon: FileText,
      prompt:
        'Draft a full creative brief targeting the highest-priority uncovered buyer scenario from the analysis above.',
    },
  ],

  meta_roas_amplify: [
    {
      id: 'generate_angles',
      label: 'Generate scale angles',
      Icon: Layers,
      prompt:
        'Generate 4 creative angles in the same winning formula identified — optimised to scale without cannibalising ROAS.',
    },
    {
      id: 'draft_brief',
      label: 'Draft amplification brief',
      Icon: FileText,
      prompt:
        'Draft a creative brief to scale the winning formula — include the formula structure, persona fit rationale, and 3 new angle directions.',
    },
  ],

  meta_account_audit: [
    {
      id: 'meta_fatigue_scan',
      label: 'Run fatigue scan',
      Icon: TrendingUp,
      prompt:
        'Now run a full Creative Fatigue Scan (Workflow 5) — check all formulas identified in the audit for frequency saturation and daily trajectory signals.',
    },
    {
      id: 'meta_diagnose_perf',
      label: 'Diagnose performance',
      Icon: BarChart2,
      prompt:
        'Run a deep performance diagnostics pass (Workflow 6) — focus on the lowest-scoring section from the audit above.',
    },
  ],
};

function usePostSkillActions(skillId: string | undefined, shortcutId?: string): QuickAction[] {
  const initial: QuickAction[] = skillId
    ? POST_ACTION_CATALOG[skillId] ?? []
    : [];
  const [actions, setActions] = useState<QuickAction[]>(initial);

  useEffect(() => {
    if (!shortcutId || !skillId) return;
    let cancelled = false;
    // The shortcut endpoint may resolve at the org level; we use the orgUid
    // from API config or default. For now, attempt the global org.
    const orgUid = (window as { __NEUROGRAPH_ORG_UID__?: string }).__NEUROGRAPH_ORG_UID__ ?? 'neurograph-global';
    fetch(`/api/v1/organizations/${encodeURIComponent(orgUid)}/skill-shortcuts/${encodeURIComponent(shortcutId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((sc) => {
        if (cancelled || !sc) return;
        const keys: string[] = sc?.ui?.post_actions ?? [];
        if (keys.length > 0) {
          const catalog = POST_ACTION_CATALOG[skillId] ?? [];
          const resolved = keys.flatMap((k) => catalog.filter((a) => a.id === k));
          if (resolved.length > 0) setActions(resolved);
        }
      })
      .catch(() => {
        // silent — keep catalog default
      });
    return () => {
      cancelled = true;
    };
  }, [skillId, shortcutId]);

  return actions;
}

function SkillCompleteBar({
  skillId,
  shortcutId,
  onAction,
}: {
  skillId?: string;
  shortcutId?: string;
  onAction: (prompt: string) => void;
}) {
  const actions = usePostSkillActions(skillId, shortcutId);
  if (!actions.length) return null;

  return (
    <div
      data-testid="post-action-bar"
      className="mx-auto mt-4 flex flex-wrap justify-center gap-2 max-w-xl"
    >
      {actions.map((action) => (
        <button
          key={action.id}
          data-testid="post-action-btn"
          onClick={() => onAction(action.prompt)}
          className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:border-brand-teal/40 hover:bg-brand-teal/5 hover:text-brand-teal transition-all shadow-sm"
        >
          <action.Icon size={11} />
          {action.label}
        </button>
      ))}
    </div>
  );
}

// Skill badge shown in the header
const SKILL_LABELS: Record<string, string> = {
  ask_persona: 'Ask the Persona',
  suggest_briefs: 'Suggest Briefs',
  scenario_explorer: 'Decision Architecture',
  draft_brief: 'Draft Brief',
  creative_review: 'Creative Review',
  seo_keyword_strategy: 'Keyword Strategy',
  // Meta Ads sub-skills
  meta_diagnose_perf:  'Performance Diagnostics',
  meta_fatigue_scan:   'Creative Fatigue Scan',
  meta_coverage_mine:  'Competitor Angle Mining',
  meta_roas_amplify:   'Formula Amplification',
  meta_account_audit:  'Account Health Audit',
};

function SkillBadge({ skillId }: { skillId: string }) {
  const label = SKILL_LABELS[skillId] ?? skillId.replace(/_/g, ' ');
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-brand-teal/10 px-3 py-1 text-xs font-semibold text-brand-teal">
      <Zap size={10} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ChatShellPage() {
  const navigate = useNavigate();
  const { threadId: routeThreadId } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();

  const clientUid = searchParams.get('client') ?? undefined;
  const personaId = searchParams.get('persona') ?? undefined;
  const triggerPrompt = searchParams.get('prompt') ?? undefined;
  const shortcutId = searchParams.get('shortcut_id') ?? undefined;
  /** Optional data-grounded signal label passed by SkillPickerPanel sub-skills, e.g. "ROAS ↓18%" */
  const signalLabel = searchParams.get('signal') ?? undefined;

  const [threadId] = useState(() => routeThreadId ?? crypto.randomUUID());
  const [messages, setMessages] = useState<ChatEntry[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [skillDone, setSkillDone] = useState(false);
  const [inputText, setInputText] = useState('');
  const [composerCursor, setComposerCursor] = useState(0);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const [interrupt, setInterrupt] = useState<InterruptPayload | null>(null);
  const [interruptInput, setInterruptInput] = useState('');
  const [provider, setProvider] = useState<ChatLlmProvider>(
    () => readChatLlmProviderCookie() ?? 'google',
  );
  // skillId is mutable state (initialized from URL, updated when a tile is launched)
  const [skillId, setSkillId] = useState<string | undefined>(
    searchParams.get('skill') ?? undefined,
  );
  const firedRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  // ---------------------------------------------------------------------------
  // Send a message (or resume from interrupt)
  // ---------------------------------------------------------------------------

  const appendAssistantChunk = (id: string, delta: unknown) => {
    const text = flattenStreamDelta(delta);
    if (!text) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id && isAssistantTextEntry(m)
          ? { ...m, content: m.content + text }
          : m,
      ),
    );
  };

  const updateToolCall = (msgId: string, toolName: string, update: Partial<ToolCall>) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== msgId || !isAssistantTextEntry(m)) return m;
        const idx = m.toolCalls.findIndex((tc) => tc.name === toolName && tc.status === 'running');
        if (idx === -1) return m;
        const toolCalls = [...m.toolCalls];
        toolCalls[idx] = { ...toolCalls[idx], ...update };
        return { ...m, toolCalls };
      }),
    );
  };

  const addArtifact = (msgId: string, artifact: Artifact) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId && isAssistantTextEntry(m)
          ? { ...m, artifacts: [...m.artifacts, artifact] }
          : m,
      ),
    );
  };

  const sendMessage = async (
    text: string,
    opts?: {
      autoFired?: boolean;
      resumeInterruptId?: string;
      skillId?: string;
      // chat-analytics-ui — context blocks injected by the SkillsRail when an
      // analytics tile is launched. The agent's `analytics_router_node` reads
      // these (esp. `chart_axis`) to auto-render the right Bonnard chart.
      contextBlocks?: { type: string; value: unknown }[];
    },
  ) => {
    if (streaming) return;
    // Use caller-supplied skillId (e.g. from tile click) or fall back to state
    const effectiveSkillId = opts?.skillId ?? skillId;

    // Always show the user message — even for autoFired (e.g. tile launches from HomeView)
    setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'user', content: text }]);
    setStreaming(true);
    setSkillDone(false);
    setInterrupt(null);

    const assistantId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', content: '', toolCalls: [], artifacts: [], skillSteps: [], isStreaming: true },
    ]);

    try {
      let resp: Response;

      if (opts?.resumeInterruptId) {
        resp = await fetch(`${getApiBase()}/api/v1/chat/resume`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            thread_id: threadId,
            interrupt_id: opts.resumeInterruptId,
            resume: { value: text },
          }),
        });
      } else {
        resp = await fetch(`${getApiBase()}/api/v1/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            thread_id: threadId,
            message: { role: 'user', content: text },
            provider,
            metadata: {
              source: 'skill_launcher',
              ...(effectiveSkillId && { skill_id: effectiveSkillId }),
              ...(clientUid && { client_uid: clientUid }),
              ...(personaId && { persona_id: personaId }),
              ...(opts?.contextBlocks?.length
                ? { context_blocks: opts.contextBlocks }
                : {}),
            },
          }),
        });
      }

      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => resp.statusText);
        throw new Error(`[${resp.status}] ${errText.slice(0, 300)}`);
      }

      // Guard: the backend must respond with an SSE stream. If we get HTML back
      // (e.g. the SPA fallback from a dev server that doesn't proxy /api/),
      // bail out immediately with a clear message instead of silently consuming it.
      const contentType = resp.headers.get('content-type') ?? '';
      if (!contentType.includes('text/event-stream')) {
        const body = await resp.text().catch(() => '');
        throw new Error(
          `Expected SSE stream (text/event-stream) but got "${contentType}". ` +
          `Is the API server running at port 8000? ` +
          (body.startsWith('<!') ? '(Got HTML — looks like a SPA fallback, not the API.)' : body.slice(0, 200)),
        );
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let sseEventCount = 0; // Track events received — if zero after EOF, something is wrong

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        // Process complete SSE blocks (separated by \n\n)
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';

        for (const block of parts) {
          const events = parseSseChunk(block + '\n\n');
          for (const { event, data } of events) {
            sseEventCount++;
            const d = data as Record<string, unknown>;

            if (event === 'message_delta') {
              appendAssistantChunk(assistantId, d.delta ?? '');
            } else if (event === 'tool_start') {
              const rawArgs = d.args;
              const argsObj =
                rawArgs !== null &&
                typeof rawArgs === 'object' &&
                !Array.isArray(rawArgs)
                  ? (rawArgs as Record<string, unknown>)
                  : rawArgs !== undefined
                    ? { input: rawArgs as unknown }
                    : undefined;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId && isAssistantTextEntry(m)
                    ? {
                        ...m,
                        toolCalls: [
                          ...m.toolCalls,
                          {
                            name: (d.name as string) ?? '',
                            status: 'running',
                            args: argsObj,
                          },
                        ],
                      }
                    : m,
                ),
              );
            } else if (event === 'tool_end') {
              const rawResult = d.result;
              const resultStr =
                typeof rawResult === 'string'
                  ? rawResult
                  : rawResult != null
                    ? JSON.stringify(rawResult, null, 2)
                    : '';
              updateToolCall(assistantId, (d.name as string) ?? '', {
                status: 'done',
                resultSummary: resultStr,
              });
            } else if (event === 'skill_step') {
              const phase = (d.phase as string) ?? '';
              const label = (d.label as string) ?? '';
              const status = (d.status as 'running' | 'done') ?? 'running';
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantId || !isAssistantTextEntry(m)) return m;
                  // Replace existing running step for this phase, or append
                  const existing = m.skillSteps.findIndex((s) => s.phase === phase && s.status === 'running');
                  const skillSteps = existing !== -1
                    ? m.skillSteps.map((s, i) => i === existing ? { phase, label, status } : s)
                    : [...m.skillSteps, { phase, label, status }];
                  return { ...m, skillSteps };
                }),
              );
            } else if (event === 'file_output') {
              addArtifact(assistantId, {
                uri: (d.uri as string) ?? '',
                mime: (d.mime as string) ?? '',
                filename: (d.filename as string) ?? '',
                kind: (d.kind as string) ?? '',
              });
            } else if (event === 'chart_output') {
              // Insert an inline ChartBubble entry. The narrative renders
              // above the chart inside the bubble itself; any subsequent
              // message_delta events continue text below it.
              const chartSpec = d.chart as import('@/services/api').WorkbenchChartSpec;
              const narrative = (d.narrative as string | undefined) ?? '';
              setMessages((prev) => [
                ...prev,
                {
                  id: crypto.randomUUID(),
                  role: 'assistant',
                  type: 'chart',
                  chart: chartSpec,
                  narrative,
                },
              ]);
            } else if (event === 'interrupt') {
              setInterrupt({
                interrupt_id: (d.interrupt_id as string) ?? '',
                kind: (d.kind as string) ?? 'input_request',
                payload: (d.payload as InterruptPayload['payload']) ?? {},
              });
            } else if (event === 'done') {
              // stream complete — surface post-skill quick actions
              setSkillDone(true);
            } else if (event === 'error') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId && isAssistantTextEntry(m)
                    ? { ...m, content: m.content + `\n\n⚠️ Error: ${(d.message as string) ?? 'Unknown error'}` }
                    : m,
                ),
              );
            }
          }
        }
      }

      // If the stream closed without emitting any SSE events, the backend is
      // likely not reachable or is misconfigured. Surface a clear message so
      // the user isn't left with a silent empty bubble.
      if (sseEventCount === 0) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && isAssistantTextEntry(m)
              ? {
                  ...m,
                  content:
                    '⚠️ The agent did not respond.\n\n' +
                    '**Check that the API server is running:**\n' +
                    '```\n./dev.sh start api\n```\n\n' +
                    'If you\'re using the Firebase dev server (port 5002), API calls aren\'t proxied to port 8000. ' +
                    'Switch to the Vite dev server:\n' +
                    '```\n./dev.sh dev-ui\n```\n' +
                    'then open **http://localhost:3000**.',
                }
              : m,
          ),
        );
      }
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId && m.role === 'assistant'
            ? { ...m, content: `⚠️ Connection error: ${String(err)}` }
            : m,
        ),
      );
    } finally {
      // Mark assistant message as no longer streaming
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId && m.role === 'assistant' ? { ...m, isStreaming: false } : m,
        ),
      );
      setStreaming(false);
    }
  };

  // Auto-fire trigger_prompt on mount, OR restore prior thread when the URL
  // already carries a /chat/:threadId path.
  useEffect(() => {
    if (firedRef.current) return;
    if (triggerPrompt && messages.length === 0) {
      firedRef.current = true;
      // Promote /chat/new → /chat/:threadId for a shareable URL without
      // triggering a React Router navigation (avoids component remount).
      if (!routeThreadId) {
        window.history.replaceState({}, '', `/chat/${threadId}?${searchParams.toString()}`);
      }
      sendMessage(triggerPrompt, { autoFired: true });
    } else if (routeThreadId && !triggerPrompt && messages.length === 0) {
      firedRef.current = true;
      // Hydrate prior conversation
      agentLabApi
        .getChatThreadHistory(routeThreadId)
        .then((res) => {
          if (!res.messages?.length) return;
          setMessages(
            res.messages.map((m) =>
              m.role === 'user'
                ? { id: crypto.randomUUID(), role: 'user' as const, content: m.content }
                  : {
                      id: crypto.randomUUID(),
                      role: 'assistant' as const,
                      content: m.content,
                      toolCalls: [],
                      artifacts: [],
                      skillSteps: [],
                    },
            ),
          );
        })
        .catch(() => {
          // silent — empty thread
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    if (!inputText.trim() || streaming) return;
    sendMessage(inputText.trim());
    setInputText('');
  };

  const handleNewThread = () => {
    navigate('/chat/new');
  };

  /**
   * Handle a skill tile click from the empty-state SkillsRail.
   *
   * Key design: we use window.history.replaceState instead of React Router's
   * navigate() so the component is NEVER unmounted. This preserves in-flight
   * state (messages, streaming) and avoids the double-fire that would occur
   * when the mount effect re-reads triggerPrompt from the updated URL.
   */
  const handleTileLaunch = (
    prompt: string,
    tileSkillId: string,
    tileSig?: string,
    extra?: { contextBlocks?: { type: string; value: unknown }[] },
  ) => {
    setSkillId(tileSkillId);
    // Stamp the skill + thread into the URL for shareability without
    // triggering a React Router navigation (which would unmount and lose state).
    const next = new URLSearchParams(searchParams);
    next.set('skill', tileSkillId);
    if (tileSig) next.set('signal', tileSig);
    window.history.replaceState({}, '', `/chat/${threadId}?${next.toString()}`);
    sendMessage(prompt, {
      skillId: tileSkillId,
      contextBlocks: extra?.contextBlocks,
    });
  };

  const { handleKeyDown: slashKeyDown, SlashMenuPortal } = useCreativeSlashMenu({
    composerRef,
    value: inputText,
    cursorPos: composerCursor,
    clientUid,
    personaId,
    mode: 'explore',
    disabled: streaming,
    onValueChange: (next, pos) => {
      setInputText(next);
      if (pos !== undefined) {
        requestAnimationFrame(() => {
          const el = composerRef.current;
          if (el) {
            el.setSelectionRange(pos, pos);
            setComposerCursor(pos);
          }
        });
      }
    },
    onPick: (pick) => {
      handleTileLaunch(pick.prompt, pick.skillId, pick.signal);
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashKeyDown(e)) return;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && inputText.trim()) {
      e.preventDefault();
      sendMessage(inputText.trim());
      setInputText('');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-slate-200/90 bg-white/90 backdrop-blur-md px-6 py-3 shrink-0 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-slate-700 transition-colors"
          title="Back"
        >
          <ArrowLeft size={18} />
        </button>

        <Bot size={16} className="text-brand-teal shrink-0" />

        {skillId && <SkillBadge skillId={skillId} />}

        {signalLabel && (
          <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-2.5 py-0.5 font-medium truncate max-w-[200px]" title={signalLabel}>
            {signalLabel}
          </span>
        )}

        {clientUid && (
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-mono">
            {clientUid}
          </span>
        )}
        {personaId && (
          <span className="text-xs text-slate-500 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-mono">
            {personaId}
          </span>
        )}

        <div className="ml-auto flex items-center gap-3">
          <ProviderToggle
            value={provider}
            onChange={(p) => {
              setProvider(p);
              writeChatLlmProviderCookie(p);
            }}
          />
          <span className="text-[10px] text-slate-400 font-mono hidden sm:block truncate max-w-24" title={threadId}>
            {threadId.slice(0, 8)}
          </span>
          <button
            onClick={handleNewThread}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            title="New session"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </header>

      {/* Message thread */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(45,212,191,0.07),transparent)] bg-slate-50/40">
        {messages.length === 0 && !streaming && (
          <div className="flex flex-col items-center justify-center h-full gap-10 py-10">
            {/* Wordmark / greeting */}
            <div className="text-center">
              <div className="w-11 h-11 rounded-2xl bg-brand-teal/10 flex items-center justify-center mx-auto mb-3">
                <Bot size={20} className="text-brand-teal/70" />
              </div>
              <p className="text-base font-semibold text-slate-700">
                {triggerPrompt ? 'Starting skill…' : 'What would you like to explore?'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Launch a skill below or type anything to get started.
              </p>
            </div>

            {/* Skill tile launcher — uses the shared smart-tiles rail */}
            {!triggerPrompt && (
              <SkillsRail
                clientUid={clientUid}
                personaId={personaId}
                onLaunch={handleTileLaunch}
                disabled={streaming}
              />
            )}
          </div>
        )}

        {messages.map((msg) => {
          // Chart bubbles are rendered full-width inline between text turns.
          if (msg.role === 'assistant' && 'type' in msg && msg.type === 'chart') {
            return (
              <div key={msg.id} className="flex gap-3 items-start">
                <div className="shrink-0 mt-1 w-8 h-8 rounded-full bg-gradient-to-br from-brand-teal/15 to-teal-400/10 ring-2 ring-white shadow-sm flex items-center justify-center">
                  <Bot size={15} className="text-brand-teal" />
                </div>
                <div className="flex-1 max-w-[80%]">
                  <ChartBubble
                    spec={msg.chart}
                    narrative={msg.narrative}
                    onSendMessage={(text, opts) => sendMessage(text, opts)}
                    threadId={threadId}
                  />
                </div>
              </div>
            );
          }
          return (
          <div key={msg.id} className={clsx('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start items-start')}>
            {msg.role === 'assistant' && (
              <div className="shrink-0 mt-1 w-8 h-8 rounded-full bg-gradient-to-br from-brand-teal/15 to-teal-400/10 ring-2 ring-white shadow-sm flex items-center justify-center">
                <Bot size={15} className="text-brand-teal" />
              </div>
            )}

            <div
              className={clsx(
                'max-w-[min(92%,52rem)] rounded-2xl px-4 py-3 text-sm transition-shadow',
                msg.role === 'user'
                  ? 'bg-gradient-to-br from-brand-teal to-teal-600 text-white shadow-lg shadow-brand-teal/20'
                  : 'bg-white/95 backdrop-blur-sm border border-slate-200/90 shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)]',
              )}
            >
              {msg.role === 'user' ? (
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              ) : (
                <>
                  {msg.skillSteps.length > 0 && <SkillStepsStrip steps={msg.skillSteps} />}
                  {/* Tool calls — shown above the response text */}
                  {msg.toolCalls.length > 0 && (
                    <div className="mb-3 space-y-1.5">
                      {msg.toolCalls.map((tc, i) => (
                        <ToolCallBubble key={`${tc.name}-${i}`} tc={tc} />
                      ))}
                    </div>
                  )}

                  {/* Streaming text — flattenStreamDelta hardens against corrupted/non-string state */}
                  {msg.content && (
                    <div className="text-slate-700 leading-relaxed whitespace-pre-wrap prose prose-sm prose-slate max-w-none [&_p]:my-2 [&_strong]:text-slate-800">
                      {flattenStreamDelta(msg.content)}
                      {msg.isStreaming && (
                        <span className="inline-block w-1.5 h-4 bg-brand-teal ml-0.5 animate-pulse rounded-sm align-middle shadow-[0_0_8px_rgba(45,212,191,0.6)]" />
                      )}
                    </div>
                  )}

                  {/* Show a cursor / working indicator while streaming but no text yet */}
                  {msg.isStreaming && !msg.content && (
                    msg.toolCalls.some((tc) => tc.status === 'running') ? (
                      <div className="flex items-center gap-3 py-1 text-xs text-slate-500">
                        <Loader2 size={14} className="animate-spin text-brand-teal shrink-0" />
                        <span className="font-medium text-slate-600">Running tools</span>
                        <span className="flex gap-1 items-center" aria-hidden>
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className="inline-block w-1.5 h-1.5 rounded-full bg-brand-teal/80 animate-bounce"
                              style={{ animationDelay: `${i * 120}ms` }}
                            />
                          ))}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 py-1">
                        <span className="inline-flex gap-1 items-center">
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className="inline-block w-1.5 h-3 rounded-sm bg-brand-teal/70 animate-pulse"
                              style={{ animationDelay: `${i * 160}ms` }}
                            />
                          ))}
                        </span>
                        <span className="text-[11px] text-slate-400 font-medium tracking-wide">Thinking</span>
                      </div>
                    )
                  )}

                  {/* Artifacts */}
                  {msg.artifacts.length > 0 && (
                    <div className="mt-3 space-y-3">
                      {msg.artifacts.map((a, i) => (
                        <ArtifactRenderer key={`${a.uri}-${i}`} artifact={a} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          );
        })}

        {streaming && messages[messages.length - 1]?.role !== 'assistant' && (
          <div className="flex gap-3 items-start">
            <div className="shrink-0 mt-1 w-8 h-8 rounded-full bg-gradient-to-br from-brand-teal/15 to-teal-400/10 ring-2 ring-white shadow-sm flex items-center justify-center">
              <Bot size={15} className="text-brand-teal" />
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200/90 bg-white/95 shadow-[0_4px_24px_-8px_rgba(15,23,42,0.1)] px-5 py-3.5 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin text-brand-teal shrink-0" />
              <span className="font-medium text-slate-600">Thinking</span>
              <span className="flex gap-1 items-center" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="inline-block w-1.5 h-1.5 rounded-full bg-brand-teal/70 animate-bounce"
                    style={{ animationDelay: `${i * 120}ms` }}
                  />
                ))}
              </span>
            </div>
          </div>
        )}

        {!streaming && skillDone && messages.length > 0 && (
          <SkillCompleteBar
            skillId={skillId}
            shortcutId={shortcutId}
            onAction={(prompt) => sendMessage(prompt)}
          />
        )}

        <div ref={bottomRef} />
      </div>

      {/* Interrupt overlay */}
      {interrupt && (
        <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 shrink-0">
          <p className="text-xs text-amber-700 font-semibold mb-2">
            The agent needs your input:
          </p>
          {interrupt.payload.value !== undefined && (
            <p className="text-sm text-amber-800 mb-2">{String(interrupt.payload.value)}</p>
          )}
          <div className="flex gap-2">
            <input
              className="flex-1 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="Your response…"
              value={interruptInput}
              onChange={(e) => setInterruptInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && interruptInput.trim()) {
                  sendMessage(interruptInput.trim(), { resumeInterruptId: interrupt.interrupt_id });
                  setInterruptInput('');
                }
              }}
            />
            <button
              onClick={() => {
                if (interruptInput.trim()) {
                  sendMessage(interruptInput.trim(), { resumeInterruptId: interrupt.interrupt_id });
                  setInterruptInput('');
                }
              }}
              className="rounded-lg bg-amber-500 px-4 py-1.5 text-sm text-white font-semibold hover:bg-amber-600"
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Chat input */}
      <footer className="relative border-t border-slate-200/90 bg-white/90 backdrop-blur-md px-6 py-4 shrink-0 shadow-[0_-4px_24px_-12px_rgba(15,23,42,0.06)]">
        <div className="flex gap-3 items-end">
          <textarea
            ref={composerRef}
            className="flex-1 resize-none rounded-2xl border border-slate-200/90 bg-white px-4 py-3 text-sm shadow-inner shadow-slate-100/80 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/25 focus:border-brand-teal/40 disabled:bg-slate-50 disabled:text-slate-400 transition-colors"
            placeholder={streaming ? 'Agent is responding…' : 'Follow up… (⌘↵ to send; / for skills)'}
            rows={2}
            value={inputText}
            disabled={streaming}
            onChange={(e) => {
              setInputText(e.target.value);
              setComposerCursor(e.target.selectionStart ?? 0);
            }}
            onSelect={(e) => setComposerCursor(e.currentTarget.selectionStart ?? 0)}
            onKeyUp={(e) => setComposerCursor((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onClick={(e) => setComposerCursor((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onKeyDown={handleKeyDown}
          />
          <button
            data-testid="chat-send-btn"
            disabled={streaming || !inputText.trim()}
            onClick={handleSubmit}
            className="shrink-0 rounded-xl bg-brand-teal px-5 py-3 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-teal/90 transition-colors"
          >
            {streaming ? (
              <span data-testid="streaming-indicator" className="flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="h-1.5 w-1.5 rounded-full bg-white animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </span>
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
        <p className="mt-1.5 text-[10px] text-slate-400 text-right select-none">
          ⌘↵ to send · / for Creative Skills
        </p>
        {SlashMenuPortal}
      </footer>
    </div>
  );
}
